import React from 'react';
import { MissionMap } from '../../map/missionMap/MissionMap';
import { MissionData } from '../../types';
import { cardCss } from '../styles';
import { Badge, Button, Gold100, Gray100 } from '@traceair/tds';
import { SiteUpdatedTooltip } from '../../openMission/projectInfo/SiteUpdatedTooltip';
import { SiteAddress } from './SiteAddress';
import { useAppStore } from '../../../../../store/appStore';
import { useUiStore } from '../../../../../store/uiStore';
import { MissionWindowTab } from '../../../../../consts/missionWindowTab';
import { useHasAccessToProjectUpload } from '../../useHasAccessToProjectUpload';
import { getMissionHtmlId, getSiteHtmlId } from '../../helpers/htmlIds';
import { MissionCardBadges } from './badges/MissionCardBadges';
import { determineIsMissionCompleted } from '../../helpers/completion';
import { classes } from 'typestyle';
import { useCalculatedUploadState } from './useCalculatedUploadState';
import { determineBadges } from './badges/determineBadges';
import { MissionUploadInterrupted } from './MissionUploadInterrupted';
import { MissionUploadProgress } from './MissionUploadProgress';
import { useCalculatedGcpRequestState } from '../../openMission/gcpRequestUpload/gcpRequestSubmit/useCalculatedGcpRequestState';
import { MissionUploadOverdue } from './MissionUploadOverdue';
import { useAreUploadsOverdue } from './useAreUploadsOverdue';
import { getUploadValidationStatus } from '../../helpers/uploadValidation';
import { useUploadStore } from '../../../../../store/uploadStore';
import { MissionCardHighlightSearch } from './MissionCardHighlightSearch';
import { MissionCardMenu } from './MissionCardMenu';
import { formatDate } from '../../../../../libs/dates';
import { ArrowForwardIcon } from '@traceair/webapp-icons';
import { useGuardUnsavedGcp } from '../../../../../hooks/useGuardUnsavedGcp';
import FlakeIcon from '../../../../../images/components/FlakeIcon';
import { useWinterConditions } from '../../useWinterConditions';

interface Props extends MissionData {}

const determineIsMissionSelected = (mission: MissionData, selectedMission: MissionData | null) => {
  if (mission.schedule?.airtableRecordId) {
    return mission.schedule?.airtableRecordId === selectedMission?.schedule?.airtableRecordId;
  }
  if (mission.gcpPlacementService?.airtableRecordId) {
    return mission.gcpPlacementService?.airtableRecordId === selectedMission?.gcpPlacementService?.airtableRecordId;
  }

  return mission.site.id === selectedMission?.site.id && mission.customer.id === selectedMission.customer.id;
};

export const MissionCard = ({ customer, site, schedule, gcpPlacementService }: Props) => {
  const { selectedMission, setSelectedMission } = useAppStore();
  const { setSelectedMissionWindowTab } = useUiStore();
  const { getUpload } = useUploadStore();
  const withGcpUnsavedChangesGuard = useGuardUnsavedGcp();

  const canAccessProjectScanUpload = useHasAccessToProjectUpload();

  const scheduleOrGcp = schedule ?? gcpPlacementService;
  const id = scheduleOrGcp ? getMissionHtmlId(scheduleOrGcp.airtableRecordId) : getSiteHtmlId(customer.id, site.id);

  const isSelected = determineIsMissionSelected({ customer, site, schedule, gcpPlacementService }, selectedMission);
  const isCompleted = determineIsMissionCompleted({ customer, site, schedule, gcpPlacementService });

  const scanUpload = getUpload(false, customer.id, site.id, schedule?.airtableRecordId);
  const scanValidationStatus = getUploadValidationStatus(scanUpload);

  const panoUpload = getUpload(true, customer.id, site.id, schedule?.airtableRecordId);
  const panoValidationStatus = getUploadValidationStatus(panoUpload);

  const scanState = useCalculatedUploadState(false, customer.id, site.id, schedule?.airtableRecordId);
  const panoState = useCalculatedUploadState(true, customer.id, site.id, schedule?.airtableRecordId);
  const gcpState = useCalculatedGcpRequestState(customer, site, gcpPlacementService);
  const { isShowWinterConditions } = useWinterConditions(site);

  const { isScanOverdue, isPanoOverdue, isGcpOverdue } = useAreUploadsOverdue(
    customer,
    site,
    schedule,
    gcpPlacementService,
  );

  const badges = determineBadges({
    scanState,
    panoState,
    gcpState,
    isScanOverdue,
    isPanoOverdue,
    isGcpOverdue,
    scanValidationStatus,
    panoValidationStatus,
    site,
    schedule,
    gcpPlacementService,
  });

  const hasBadges = badges.length > 0;

  const WinterConditionsMediumBadge = isShowWinterConditions ? (
    <Badge
      type="warning"
      size={'m'}
      label={'Winter conditions'}
      outlined={true}
      icon={<FlakeIcon color={Gold100} size={'16'} />}
      className={cardCss.winterConditionsMediumBadge}
    />
  ) : null;
  const WinterConditionsSmallBadge = isShowWinterConditions ? (
    <Badge
      type="warning"
      size={'s'}
      label={'Winter conditions'}
      outlined={true}
      icon={<FlakeIcon color={Gold100} size={'14'} />}
      className={cardCss.winterConditionsSmallBadge}
    />
  ) : null;

  /**
   * Handles click on the MissionCard.
   * If there are unsaved GCP changes, shows a confirmation modal before proceeding.
   * Navigates to Upload tab if the user has access or schedule/GCP exists;
   * otherwise opens the Mission Info tab.
   */
  const handleCardClick = withGcpUnsavedChangesGuard(() => {
    setSelectedMissionWindowTab(
      canAccessProjectScanUpload || !!scheduleOrGcp ? MissionWindowTab.Upload : MissionWindowTab.MissionInfo,
    );
    setSelectedMission({ customer, site, schedule, gcpPlacementService });
  });

  /**
   * Handles click on the "Mission info" button inside a MissionCard.
   * Stops propagation to avoid triggering the full card click handler.
   * If there are unsaved GCP changes, shows a confirmation modal before switching tabs.
   * Always navigates to the Mission Info tab.
   */
  const handleMissionInfoClick = withGcpUnsavedChangesGuard((event?: React.MouseEvent) => {
    event?.stopPropagation();
    setSelectedMission({ customer, site, schedule, gcpPlacementService });
    window.requestAnimationFrame(() => {
      setSelectedMissionWindowTab(MissionWindowTab.MissionInfo);
    });
  });

  return (
    <article
      id={id}
      className={classes(
        cardCss.card,
        isCompleted && scheduleOrGcp && cardCss.cardCompleted,
        isSelected && cardCss.cardSelected,
      )}
      onClick={handleCardClick}
      data-intercom-target="MissionCard"
    >
      <div className={cardCss.layout}>
        <SiteUpdatedTooltip className={cardCss.updateIndicator} site={site} />
        <MissionMap customer={customer} site={site} schedule={schedule} />

        <div className={cardCss.mainSection}>
          <header className={cardCss.headerRow}>
            <div>
              <div className={cardCss.titleGroup}>
                <h2 className={cardCss.siteName}>{<MissionCardHighlightSearch text={site.name} />}</h2>
                {WinterConditionsMediumBadge}
              </div>
              <div className={cardCss.customerName}>{<MissionCardHighlightSearch text={customer.name} />}</div>
              <div className={cardCss.winterAndMovedDateContainer}>
                {WinterConditionsSmallBadge}
                {schedule?.initialFlightDateIso && schedule.flightDateIso !== schedule.initialFlightDateIso && (
                  <>
                    <div className={classes(cardCss.initialFlightDate, cardCss.initialFlightDateLongModifier)}>
                      <ArrowForwardIcon color={Gray100} />
                      Moved from {formatDate({ date: schedule.initialFlightDateIso })}
                    </div>
                    <div className={classes(cardCss.initialFlightDate, cardCss.initialFlightDateShortModifier)}>
                      <ArrowForwardIcon color={Gray100} />
                      from {formatDate({ date: schedule.initialFlightDateIso, shortMonth: true })}
                    </div>
                  </>
                )}
              </div>
            </div>

            {(canAccessProjectScanUpload || scheduleOrGcp) && (
              <div className={cardCss.headerActions}>
                <Button
                  onClick={handleMissionInfoClick}
                  className={cardCss.missionInfo}
                  size="xs"
                  style="secondary"
                  text="Mission info"
                  data-intercom-target="MissionCardMissionInfoButton"
                />

                {schedule && (
                  <>
                    <MissionCardMenu
                      customer={customer}
                      site={site}
                      schedule={schedule}
                      gcpPlacementService={gcpPlacementService}
                    />
                  </>
                )}
              </div>
            )}
          </header>

          <footer>
            {hasBadges && <div className={classes(cardCss.missionLabel)}>Mission scope</div>}

            <div className={cardCss.footerRow}>
              {hasBadges && (
                <div className={cardCss.badgeContainer}>
                  <MissionCardBadges badges={badges} />
                </div>
              )}

              {site.projectInfo.siteAddress && (
                <div
                  className={classes(cardCss.addressContainer, !scheduleOrGcp && cardCss.addressContainerInProjects)}
                >
                  <SiteAddress alignRight={!!scheduleOrGcp} site={site} />
                </div>
              )}
            </div>
          </footer>
        </div>
      </div>

      {site.hasUploadPermission && (
        <>
          <MissionUploadProgress
            isPano={false}
            customer={customer}
            site={site}
            schedule={schedule}
            validationStatus={scanValidationStatus}
          />

          <MissionUploadProgress
            isPano={true}
            customer={customer}
            site={site}
            schedule={schedule}
            validationStatus={panoValidationStatus}
          />

          <MissionUploadInterrupted scanState={scanState} panoState={panoState} />

          <MissionUploadOverdue
            isScanOverdue={isScanOverdue}
            isPanoOverdue={isPanoOverdue}
            isGcpOverdue={isGcpOverdue}
          />
        </>
      )}
    </article>
  );
};
